import { useState } from 'react';
import { motion, useReducedMotion, Pressable, FadeIn, springs } from '../lib/motion';

/** Floating brand orbs that give the hero a little life. Decorative only. */
const ORBS = [
  { cls: 'bg-squishy-teal',     size: 384, top: '-8rem',  right: '-6rem', opacity: 0.20, dur: 11, drift: 26 },
  { cls: 'bg-squishy-purple',   size: 320, top: '32%',    left: '-8rem',  opacity: 0.26, dur: 14, drift: -32 },
  { cls: 'bg-squishy-gold',     size: 200, top: '14%',    right: '2rem',  opacity: 0.16, dur: 9,  drift: 22 },
  { cls: 'bg-squishy-tealDark', size: 384, bottom: '-10rem', left: '30%', opacity: 0.30, dur: 13, drift: -24 },
] as const;

/**
 * Shared visual frame for all auth pages: a vibrant navy-teal-purple brand
 * gradient with drifting orbs and a lively logo. Premium and polished, but
 * energetic and inviting: this is the product's first impression.
 */
export function AuthShell({ children }: { children: React.ReactNode }) {
  const reduce = useReducedMotion();
  return (
    <div
      className="relative min-h-[100dvh]"
      style={{
        background:
          'linear-gradient(160deg, #1e1b4b 0%, #312e81 34%, #4c1d95 60%, #0f766e 90%, #0d9488 120%)',
      }}
    >
      {/* Drifting brand orbs */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {ORBS.map((o, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full blur-3xl ${o.cls}`}
            style={{
              width: o.size, height: o.size,
              top: 'top' in o ? o.top : undefined,
              bottom: 'bottom' in o ? o.bottom : undefined,
              left: 'left' in o ? o.left : undefined,
              right: 'right' in o ? o.right : undefined,
              opacity: o.opacity,
            }}
            animate={reduce ? undefined : { y: [0, o.drift, 0], x: [0, o.drift / 2, 0] }}
            transition={{ duration: o.dur, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>

      <main
        className="relative z-10 mx-auto flex min-h-[100dvh] w-full max-w-md flex-col items-center justify-center px-4"
        style={{
          paddingTop: 'max(2.5rem, env(safe-area-inset-top))',
          paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))',
        }}
      >
        <motion.div
          className="flex flex-col items-center text-center"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springs.soft}
        >
          <motion.img
            src="/logo.png"
            alt="Squishy School"
            className="h-20 w-20"
            style={{ filter: 'drop-shadow(0 14px 28px rgba(0, 0, 0, 0.4))' }}
            initial={{ scale: 0.6, rotate: -8, opacity: 0 }}
            animate={reduce
              ? { scale: 1, rotate: 0, opacity: 1 }
              : { scale: 1, rotate: 0, opacity: 1, y: [0, -7, 0] }}
            transition={reduce
              ? springs.bouncy
              : { scale: springs.bouncy, rotate: springs.bouncy, opacity: { duration: 0.3 }, y: { duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: 0.6 } }}
          />
          <h2 className="mt-3 font-display text-[26px] font-semibold tracking-wide text-white">
            Squishy School
          </h2>
          <p className="mt-1 text-[13px] font-medium text-white/70">
            Ace NCLEX, USMLE, PANCE, and the bar
          </p>
        </motion.div>

        {children}

        <p className="mt-7 text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-white/40">
          Practice. Reason. Pass.
        </p>
      </main>
    </div>
  );
}

export function AuthCard({
  title, subtitle, children,
}: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ ...springs.soft, delay: 0.05 }}
      className="mt-6 w-full rounded-3xl bg-white p-7 shadow-2xl ring-1 ring-black/5"
    >
      <h1 className="text-[22px] font-bold tracking-tight text-squishy-navy">{title}</h1>
      {subtitle && <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </motion.div>
  );
}

export function AuthInput(props: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  autoFocus?: boolean;
  required?: boolean;
  placeholder?: string;
  error?: string;
  /** Visual only: extra classes for the input element (e.g. code inputs). */
  inputClassName?: string;
  /** Visual only: mobile keyboard hint. */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}) {
  const [show, setShow] = useState(false);
  const isPassword = props.type === 'password';
  const resolvedType = isPassword ? (show ? 'text' : 'password') : (props.type ?? 'text');

  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{props.label}</span>
      <div className="relative mt-1.5">
        <input
          type={resolvedType}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          autoComplete={props.autoComplete}
          autoFocus={props.autoFocus}
          required={props.required}
          placeholder={props.placeholder}
          inputMode={props.inputMode}
          className={`min-h-[48px] w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 transition-all duration-150 focus:border-squishy-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-squishy-primary/20 ${isPassword ? 'pr-12' : ''} ${props.inputClassName ?? ''}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-slate-400 transition-colors hover:text-slate-600"
          >
            {show ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        )}
      </div>
      {props.error && <span className="mt-1 block text-xs text-red-600">{props.error}</span>}
    </label>
  );
}

export function AuthButton({ children, loading, type = 'submit', disabled, onClick }: {
  children: React.ReactNode;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Pressable
      type={type}
      disabled={loading || disabled}
      onClick={onClick}
      haptic="medium"
      className="relative flex min-h-[52px] w-full items-center justify-center rounded-xl bg-gradient-to-r from-squishy-tealDark to-squishy-teal text-[15px] font-semibold text-white shadow-pop transition-shadow hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? (
        <>
          <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-90" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <span className="sr-only">Loading</span>
        </>
      ) : children}
    </Pressable>
  );
}

export function AuthError({ message }: { message: string }) {
  return (
    <motion.div
      key={message}
      role="alert"
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0, x: [0, -6, 6, -4, 4, 0] }}
      transition={{ duration: 0.4 }}
      className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9.344 3.721c.866 1.5-.217 3.374-1.948 3.374H4.604c-1.73 0-2.813-1.874-1.948-3.374L10.052 3.03c.866-1.5 3.03-1.5 3.896 0l7.396 12.816zM12 15.75h.007v.008H12v-.008z" />
      </svg>
      <span>{message}</span>
    </motion.div>
  );
}

export function AuthSuccess({ message }: { message: string }) {
  return (
    <motion.div
      key={message}
      role="status"
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.soft}
      className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>{message}</span>
    </motion.div>
  );
}
